import axios from 'axios'

/**
 * Service to detect when the running app bundle is out of date and reload it.
 *
 * How it works:
 * - BUILD_HASH below is a placeholder that post-build.js replaces, inside the
 *   compiled app bundle, with the real bundle hash. So the RUNNING bundle knows
 *   its own version, even when it was served from a (WebView / browser) cache.
 * - We compare that baked-in hash against the freshly fetched /version.json hash.
 *   If they differ, the loaded bundle is stale and we force a cache-busting reload.
 *
 * This fixes the previous implementation, which initialised its "current" hash
 * from /version.json itself (always fresh) and therefore could never tell that
 * the actually-running bundle was stale - it only caught deploys that happened
 * while a session stayed open.
 */

// Replaced at build time by post-build.js. Stays as the placeholder in dev builds.
const BUILD_HASH = '{{POST_BUILD_ENTERS_HASH_HERE}}'
const RELOAD_GUARD_KEY = 'vc_reload_target'

class VersionChecker {
  constructor () {
    this.buildHash = BUILD_HASH
    this.checkInterval = null
    this.initialized = false
  }

  // Dev build (no post-build step): the placeholder was never replaced.
  // Disable checking so we never reload-loop locally.
  hashUnavailable () {
    return !this.buildHash || this.buildHash.indexOf('POST_BUILD_ENTERS_HASH_HERE') !== -1
  }

  /**
   * @param {Object} options
   * @param {number} options.interval Check interval in ms (default 2 min)
   * @param {boolean} options.autoReload Reload automatically on a new version (default true)
   * @param {Function} options.onUpdateDetected Called with { version, hash, buildHash } on mismatch
   */
  init (options) {
    if (this.initialized) return
    this.initialized = true

    if (this.hashUnavailable()) return

    const config = Object.assign({
      interval: 2 * 60 * 1000,
      autoReload: true,
      onUpdateDetected: null
    }, options || {})

    // Check immediately - this is what catches a stale bundle loaded from cache -
    // then keep checking for deploys that happen during the session.
    this.checkForUpdates(config)
    this.checkInterval = setInterval(() => this.checkForUpdates(config), config.interval)
  }

  checkForUpdates (config) {
    this.fetchVersionInfo()
      .then(versionInfo => {
        if (!versionInfo || !versionInfo.hash) return

        if (versionInfo.hash === this.buildHash) {
          // We are current - allow future updates to reload again.
          this.clearReloadGuard()
          return
        }

        if (typeof config.onUpdateDetected === 'function') {
          config.onUpdateDetected(Object.assign({}, versionInfo, { buildHash: this.buildHash }))
        }

        if (config.autoReload) {
          this.reloadIfNotLooping(versionInfo.hash)
        }
      })
      .catch(error => {
        console.error('VersionChecker: update check failed', error)
      })
  }

  // Guard against an infinite reload loop if, after reloading, the fresh bundle
  // still cannot be fetched (e.g. CDN not yet propagated).
  reloadIfNotLooping (serverHash) {
    let lastTarget = null
    try { lastTarget = window.sessionStorage.getItem(RELOAD_GUARD_KEY) } catch (e) {}

    if (lastTarget === serverHash) {
      console.warn('VersionChecker: bundle still stale after reload; manual refresh required')
      return
    }

    try { window.sessionStorage.setItem(RELOAD_GUARD_KEY, serverHash) } catch (e) {}
    this.cacheBustReload(serverHash)
  }

  clearReloadGuard () {
    try { window.sessionStorage.removeItem(RELOAD_GUARD_KEY) } catch (e) {}
  }

  // A changing query param makes the URL unique, so the WebView / browser HTTP
  // cache and the CDN are forced to refetch index.html (and the new bundle).
  cacheBustReload (serverHash) {
    try {
      const url = new URL(window.location.href)
      url.searchParams.set('_v', serverHash)
      window.location.replace(url.toString())
    } catch (e) {
      const sep = window.location.href.indexOf('?') === -1 ? '?' : '&'
      window.location.replace(window.location.href + sep + '_v=' + serverHash)
    }
  }

  fetchVersionInfo () {
    const timestamp = new Date().getTime() // bypass any caching of version.json
    return axios.get('/version.json?t=' + timestamp)
      .then(response => response.data)
      .catch(error => {
        console.error('VersionChecker: error fetching version info', error)
        return null
      })
  }

  stop () {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }
  }
}

export default new VersionChecker()
